import { LOCALES, LOCALE_LABELS, isLocale, useLocale, useT } from '@planetra/i18n';
import {
  Menu,
  MenuItem,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
} from '@planetra/ui';
import { LogOut, MonitorSmartphone } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useRevalidator } from 'react-router';
import { ROUTES } from '../app/routes';
import { sessionStore, signOut, useSession } from '../session';

/**
 * The account menu in the header (P0-WEB-08, PRD §AUTH-8).
 *
 * `AUTH-8` asks for a top-right avatar holding profile, language, the
 * organisation switcher, sessions and sign-out. It shipped in P0-WEB-07 as two
 * plain header controls because `packages/ui` had no menu primitive; P1-WEB-03
 * built one, so this is the menu the requirement actually asked for.
 *
 * **Profile is the identity block, not a page.** `AUTH-8` lists "profile", but
 * the Platform API exposes `GET /v1/auth/me` and nothing that writes to it —
 * no `PATCH`, no avatar upload. A menu entry leading to a form that cannot
 * save is worse than no entry, so the name and email are shown and there is
 * nowhere to click. When an update endpoint exists, this is where the link
 * goes.
 *
 * **Language is radio items rather than the `LocaleSwitcher`.** That component
 * is a Radix `Select`, and a listbox inside an open dropdown menu fights it for
 * focus — the menu closes on the first arrow key. Radio items are the pattern a
 * menu already supports, and they announce the current choice. `LocaleSwitcher`
 * stays where it works, on the anonymous auth pages.
 */
export function AccountMenu() {
  const { t } = useT();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { user, organisations, organisationId } = useSession();
  const { locale, setLocale } = useLocale();
  const [pending, setPending] = useState(false);

  async function endSession() {
    if (pending) return;
    setPending(true);

    // The result is not branched on. `signOut` clears the local session either
    // way — a browser told to sign out must let go of the session whether or
    // not the server confirmed, and the cookie is HttpOnly so nothing here can
    // drop it independently.
    await signOut();
    await navigate(ROUTES.signIn, { replace: true });
  }

  function switchOrganisation(next: string) {
    if (next === organisationId) return;

    sessionStore.selectOrganisation(next);

    // Every loader on the page fetched its data under the old
    // `X-Organisation-Id`. Without this the header renames itself and the
    // projects underneath it stay the previous council's — the most
    // convincing possible way to show somebody another tenant's work.
    void revalidator.revalidate();
  }

  return (
    <Menu
      label={t('account:menu.label')}
      trigger={
        <button
          type="button"
          // No focus styling: theme.css sets one global ring for NFR-11, and a
          // per-component override is how that stops being global.
          className="flex max-w-56 items-center gap-2 rounded-full py-1 pr-3 pl-1 text-sm hover:bg-surface-raised"
        >
          <span
            aria-hidden
            className="grid size-7 shrink-0 place-items-center rounded-full bg-accent-soft text-xs font-semibold text-accent"
          >
            {initials(user?.name)}
          </span>
          {user === undefined ? null : (
            // Truncated, not wrapped: the header has a fixed height and a long
            // name must not change it.
            <span className="truncate text-fg-muted">{user.name}</span>
          )}
        </button>
      }
      className="min-w-64"
    >
      {user === undefined ? null : (
        <>
          {/* Not a MenuItem: there is nothing to activate, and a focusable row
              that does nothing is a dead stop for a keyboard user. */}
          <div className="px-2.5 py-2">
            <p className="truncate text-sm font-medium text-fg">{user.name}</p>
            <p className="truncate text-xs text-fg-muted">{user.email}</p>
          </div>
          <MenuSeparator />
        </>
      )}

      {/* One organisation is not a switcher. The header already names it, and a
          radio group with a single option is a control that cannot do
          anything. */}
      {organisations.length > 1 ? (
        <>
          <MenuLabel>{t('account:menu.organisation')}</MenuLabel>
          <MenuRadioGroup value={organisationId ?? ''} onValueChange={switchOrganisation}>
            {organisations.map((organisation) => (
              <MenuRadioItem key={organisation.id} value={organisation.id}>
                <span className="truncate">{organisation.name}</span>
              </MenuRadioItem>
            ))}
          </MenuRadioGroup>
          <MenuSeparator />
        </>
      ) : null}

      <MenuLabel>{t('common:language.label')}</MenuLabel>
      <MenuRadioGroup
        value={locale}
        onValueChange={(next) => {
          // The radio value is a bare string; narrowing here keeps an
          // impossible value from reaching a store that persists it.
          if (isLocale(next)) setLocale(next);
        }}
      >
        {LOCALES.map((option) => (
          // Each option in its own language: offering "Malay" to somebody who
          // only reads Malay has failed at the one thing it does.
          <MenuRadioItem key={option} value={option}>
            {LOCALE_LABELS[option]}
          </MenuRadioItem>
        ))}
      </MenuRadioGroup>

      <MenuSeparator />

      <MenuItem asChild icon={MonitorSmartphone}>
        <Link to={ROUTES.accountSessions}>{t('account:menu.sessions')}</Link>
      </MenuItem>

      <MenuItem
        icon={LogOut}
        tone="danger"
        disabled={pending}
        // Radix closes the menu before the handler settles otherwise, which
        // unmounts the component mid-await and loses the navigation.
        onSelect={(event) => {
          event.preventDefault();
          void endSession();
        }}
      >
        {pending ? t('account:menu.signingOut') : t('account:menu.signOut')}
      </MenuItem>
    </Menu>
  );
}

/**
 * Up to two initials, or a placeholder before the name has loaded.
 *
 * Takes the first and last word rather than the first two: Malaysian names
 * carry patronymics — "Nurul Aisyah binti Rahman" — and "NA" says less than
 * "NR" about which colleague this is.
 */
function initials(name: string | undefined): string {
  if (name === undefined) return '·';

  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '·';

  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words.at(-1)?.[0] ?? '') : '';

  return (first + last).toUpperCase();
}
